import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useAgentExecutionClient } from "src/agents/queries/execution";
import { DecisionEnvironment } from "src/api/types";
import type {
  AgentHistoryResponse,
  Model,
  SessionResponse,
  SessionStatus,
  SortDirection,
  SortField,
  TriggerSourceType,
} from "src/clients/agent-management";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UseAgentHistoryParams = {
  agentId: string;
  environment: DecisionEnvironment;
  from: string; // ISO string
  to: string; // ISO string
  sort: { field: SortField; direction: SortDirection };
  status?: SessionStatus;
  agent_version_id?: string;
  origin?: TriggerSourceType;
  model?: string;
  page_size: number;
  searchSessionId?: string | null;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PREFIX_CACHE_KEY = "agent-history";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useAgentHistory = (params: UseAgentHistoryParams) => {
  const {
    agentId,
    environment,
    from,
    to,
    sort,
    status,
    agent_version_id: version,
    origin,
    model,
    page_size,
    searchSessionId,
  } = params;

  const client = useAgentExecutionClient();
  const isSearching = !!searchSessionId;

  const apiEnvironment =
    environment === DecisionEnvironment.LIVE ? "production" : "sandbox";

  const listQuery = useInfiniteQuery({
    queryKey: [
      PREFIX_CACHE_KEY,
      "sessions",
      agentId,
      environment,
      from,
      to,
      sort?.field,
      sort?.direction,
      status,
      version,
      origin,
      model,
      page_size,
    ],
    queryFn: async ({ pageParam }): Promise<AgentHistoryResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      const response =
        await client.listSessionsAgentsManagementApiV0AgentsAgentIdSessionsGet({
          agentId,
          environment: apiEnvironment,
          from,
          to,
          cursor: pageParam,
          pageSize: page_size,
          sortField: sort?.field,
          sortDirection: sort?.direction,
          status: status ?? undefined,
          agentVersionId: version ?? undefined,
          origin: origin ?? undefined,
          model: (model as Model) ?? undefined,
        });
      return response.data;
    },
    enabled: !!client && !isSearching,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    select: (data) => ({
      sessions: data.pages.flatMap((p) => p.sessions),
      metrics: data.pages[data.pages.length - 1].metrics,
      total_count: data.pages[data.pages.length - 1].total_count,
    }),
  });

  const searchQuery = useQuery({
    queryKey: [
      PREFIX_CACHE_KEY,
      "session-search",
      agentId,
      apiEnvironment,
      searchSessionId,
    ],
    queryFn: async (): Promise<SessionResponse | null> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!searchSessionId) {
        throw new Error("Session ID is required");
      }
      try {
        const response =
          await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
            sessionId: searchSessionId,
            includeMessages: false,
          });
        if (
          response.data.agent_id !== agentId ||
          response.data.environment !== apiEnvironment
        ) {
          return null;
        }
        return response.data;
      } catch (error) {
        if (
          isAxiosError(error) &&
          [404, 422].includes(error.response?.status ?? 0)
        ) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!client && isSearching,
  });

  if (isSearching) {
    return {
      data: searchQuery.data
        ? {
            sessions: [searchQuery.data],
            metrics: {},
            total_count: 1,
          }
        : undefined,
      isLoading: searchQuery.isLoading,
      isError: searchQuery.isError,
      fetchNextPage: () => {},
      isFetchingNextPage: false,
      refetch: searchQuery.refetch,
      isRefetching: searchQuery.isRefetching,
    };
  } else {
    return listQuery;
  }
};
